// PURPOSE: Shared transient feedback and undo. CONTRACT: spec 078 A1, at most three messages. DEPENDS: React, API error labels. CEILING: callers own authorization and inverse requests.
import { useEffect, useRef, useState, useSyncExternalStore, type CSSProperties } from 'react';
import { errorMessage } from './api';
import './toast.css';
type ToastOptions = { message: string; undo?: () => Promise<void> | void; duration?: number; persistent?: boolean; action?: { label: string; run(): Promise<void> | void; href?: string } };
type Toast = ToastOptions & { id: number; duration: number; phase: 'ready' | 'busy' | 'error'; error?: string };
let sequence=0;
let messages: Toast[]=[];
const listeners=new Set<()=>void>();
const emit=()=>listeners.forEach(listener=>listener());
const subscribe=(listener:()=>void)=>{listeners.add(listener);return()=>{listeners.delete(listener);};};
const snapshot=()=>messages;
const dismiss=(id:number)=>{messages=messages.filter(item=>item.id!==id);emit();};
const show=({duration=10000,...options}:ToastOptions):number=>{
 messages=[...messages,{...options,duration:Number.isFinite(duration) && duration>0?duration:10000,id:++sequence,phase:'ready' as const}].slice(-3);emit();return sequence;
};
async function undoToast(id:number) {
 const item=messages.find(item=>item.id===id);
 if(!item?.undo || item.phase!=='ready')return;
 messages=messages.map(toast=>toast.id===id?{...toast,phase:'busy'}:toast);emit();
 try { await item.undo();dismiss(id); }
 catch(error) { messages=messages.map(toast=>toast.id===id?{...toast,phase:'error',error:errorMessage(error),undo:undefined}:toast);emit(); }
}
const update=(id:number,options:Partial<ToastOptions>)=>{messages=messages.map(item=>item.id===id?{...item,...options}:item);emit();};
async function runAction(id:number) {
 const item=messages.find(item=>item.id===id);if(!item?.action || item.phase==='busy')return;
 messages=messages.map(item=>item.id===id?{...item,phase:'busy'}:item);emit();
 try {await item.action.run();} catch {update(id,{message:'Could not complete this action. Try again.'});}
 finally {messages=messages.map(item=>item.id===id?{...item,phase:'ready'}:item);emit();}
}
// Call show after the original write succeeds; undo must send the authorized inverse with a fresh key.
export function useToast() { return {show,update,dismiss}; }
function blocked(event:KeyboardEvent) {
 const target=event.target instanceof Element?event.target:document.activeElement;
 return event.defaultPrevented || event.isComposing || !!target?.closest('input,textarea,select,[contenteditable]:not([contenteditable="false"])') || !!document.querySelector('dialog[open], [role="dialog"]:not([hidden]), [role="alertdialog"]:not([hidden])');
}
function ToastItem({item}:{item:Toast}) {
 const [hover,setHover]=useState(false),[focus,setFocus]=useState(false);
 const remaining=useRef(item.duration);
 const paused=hover || focus || item.phase==='busy';
 useEffect(()=>{
  if(paused || item.persistent)return;
  const start=Date.now(),timer=window.setTimeout(()=>dismiss(item.id),remaining.current);
  return()=>{window.clearTimeout(timer);remaining.current=Math.max(0,remaining.current-(Date.now()-start));};
 },[paused,item.id,item.persistent]);
 return <div className="toast" role="status" aria-atomic="true" onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)} onFocusCapture={()=>setFocus(true)} onBlurCapture={event=>{if(!event.currentTarget.contains(event.relatedTarget as Node|null))setFocus(false);}}>
  <div className="toast-content"><span>{item.message}{item.error && <span className="toast-error">{item.error}</span>}</span>{item.action && (item.action.href ? <a href={item.action.href} aria-disabled={item.phase==='busy'} onClick={event=>{event.preventDefault();void runAction(item.id);}}>{item.action.label}</a> : <button type="button" disabled={item.phase==='busy'} aria-busy={item.phase==='busy'} onClick={()=>void runAction(item.id)}>{item.action.label}</button>)}{item.undo && <button type="button" disabled={item.phase==='busy'} aria-busy={item.phase==='busy'} onClick={()=>void undoToast(item.id)}>{item.phase==='busy'?'Undoing...':'Undo'}</button>}</div>
  {!item.persistent && <div className="toast-countdown" aria-hidden="true" style={{'--toast-duration':`${item.duration}ms`,animationPlayState:paused?'paused':'running'} as CSSProperties} />}
 </div>;
}
// Mount once in the authenticated shell; the sibling hook needs no provider or page wiring.
export function ToastHost() {
 const items=useSyncExternalStore(subscribe,snapshot,snapshot);
 useEffect(()=>{
  const onKey=(event:KeyboardEvent)=>{
   if(blocked(event) || event.altKey || event.shiftKey)return;
   if(event.key==='Escape' && !event.ctrlKey && !event.metaKey && messages.length){event.preventDefault();dismiss(messages[messages.length-1].id);}
   else if(event.key.toLowerCase()==='z' && (event.ctrlKey || event.metaKey)){
    const item=[...messages].reverse().find(item=>item.undo && item.phase==='ready');
    if(item){event.preventDefault();void undoToast(item.id);}
   }
  };
  document.addEventListener('keydown',onKey);
  return()=>{document.removeEventListener('keydown',onKey);messages=[];emit();};
 },[]);
 return <div className="toast-host">{items.map(item=><ToastItem key={`${item.id}-${item.phase==='error'?'error':'active'}`} item={item} />)}</div>;
}
